import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/apiFetch';
import {
  libraryTreeResponseSchema,
  programDetailResponseSchema,
  trainingSectionsResponseSchema,
  employeeTrainingProfileResponseSchema,
  trainingEmployeesResponseSchema,
  employeeAssignmentsResponseSchema,
  assignmentDetailResponseSchema,
  assignmentEventsResponseSchema,
  trainingSummaryWidgetResponseSchema,
  trainingRiskWidgetResponseSchema,
  problemEmployeesResponseSchema,
  trainingMatrixResponseSchema,
  type LibraryTreeItem,
  type ProgramDetail,
  type TrainingSection,
  type EmployeeTrainingProfile,
  type TrainingEmployeeRow,
  type EmployeeAssignment,
  type AssignmentDetail,
  type AssignmentEvent,
  type TrainingSummaryWidget,
  type TrainingRiskWidget,
  type ProblemEmployeeRow,
  type ProblemEmployeesSummary,
  type MatrixPosition,
} from '@/entities/training/model/librarySchema';

interface LibraryTreeParams {
  rootId?: string | null;
  q?: string;
  programKind?: string[];
  recurrenceModel?: string[];
  levelBased?: boolean;
}

async function fetchLibraryTree(params: LibraryTreeParams): Promise<LibraryTreeItem[]> {
  const searchParams = new URLSearchParams();
  if (params.rootId) searchParams.set('root_id', params.rootId);
  if (params.q) searchParams.set('q', params.q);
  if (params.programKind) {
    params.programKind.forEach((v) => searchParams.append('program_kind', v));
  }
  if (params.recurrenceModel) {
    params.recurrenceModel.forEach((v) => searchParams.append('recurrence_model', v));
  }
  if (params.levelBased !== undefined) {
    searchParams.set('level_based', String(params.levelBased));
  }

  const qs = searchParams.toString();
  const path = `/training/library/tree${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  const parsed = libraryTreeResponseSchema.parse(raw);
  return parsed.items;
}

async function fetchProgramDetail(programId: string): Promise<ProgramDetail> {
  const raw = await apiFetch<unknown>(`/training/programs/${programId}`);
  const parsed = programDetailResponseSchema.parse(raw);
  return parsed.program;
}

export function useLibraryTree(params: LibraryTreeParams) {
  return useQuery({
    queryKey: ['training', 'library', 'tree', params],
    queryFn: () => fetchLibraryTree(params),
    placeholderData: keepPreviousData,
  });
}

export function useProgramDetail(programId: string | null) {
  return useQuery({
    queryKey: ['training', 'programs', programId],
    queryFn: () => fetchProgramDetail(programId!),
    enabled: !!programId,
  });
}

async function fetchTrainingSections(): Promise<TrainingSection[]> {
  const raw = await apiFetch<unknown>('/training/sections');
  const parsed = trainingSectionsResponseSchema.parse(raw);
  return parsed.sections;
}

/** GET /v1/training/sections — подразделы раздела «Обучение» по роли. */
export function useTrainingSections() {
  return useQuery({
    queryKey: ['training', 'sections'],
    queryFn: fetchTrainingSections,
    staleTime: 60_000,
  });
}

async function fetchEmployeeTrainingProfile(employeeId: string): Promise<EmployeeTrainingProfile> {
  const raw = await apiFetch<unknown>(`/training/employees/${employeeId}`);
  const parsed = employeeTrainingProfileResponseSchema.parse(raw);
  return parsed.employee;
}

/** GET /v1/training/employees/{employeeId} — профиль обучения сотрудника. */
export function useEmployeeTrainingProfile(employeeId: string | null) {
  return useQuery({
    queryKey: ['training', 'employees', employeeId],
    queryFn: () => fetchEmployeeTrainingProfile(employeeId!),
    enabled: !!employeeId,
  });
}

// --- List employees in training (§5.2.1) ---

/** Совпадает с `TRAINING_EMPLOYEES_PAGE_SIZE` на бэкенде. */
export const TRAINING_EMPLOYEES_PAGE_SIZE = 10;

export interface TrainingEmployeesParams {
  q?: string;
  orgUnitId?: string[];
  programKind?: string[];
  recurrenceModel?: string[];
  levelBased?: boolean;
  status?: string[];
  completedFrom?: string;
  completedTo?: string;
  validUntilFrom?: string;
  validUntilTo?: string;
  quickFilter?: string[];
  pageToken?: string;
}

export interface TrainingEmployeesResult {
  items: TrainingEmployeeRow[];
  nextPageToken: string | null;
}

async function fetchTrainingEmployees(params: TrainingEmployeesParams): Promise<TrainingEmployeesResult> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.orgUnitId) params.orgUnitId.forEach((v) => sp.append('org_unit_id', v));
  if (params.programKind) params.programKind.forEach((v) => sp.append('program_kind', v));
  if (params.recurrenceModel) params.recurrenceModel.forEach((v) => sp.append('recurrence_model', v));
  if (params.levelBased !== undefined) sp.set('level_based', String(params.levelBased));
  if (params.status) params.status.forEach((v) => sp.append('status', v));
  if (params.completedFrom) sp.set('completed_from', params.completedFrom);
  if (params.completedTo) sp.set('completed_to', params.completedTo);
  if (params.validUntilFrom) sp.set('valid_until_from', params.validUntilFrom);
  if (params.validUntilTo) sp.set('valid_until_to', params.validUntilTo);
  if (params.quickFilter) params.quickFilter.forEach((v) => sp.append('quick_filter', v));
  if (params.pageToken) sp.set('page_token', params.pageToken);

  const qs = sp.toString();
  const path = `/training/employees${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  const parsed = trainingEmployeesResponseSchema.parse(raw);
  return { items: parsed.items, nextPageToken: parsed.next_page_token ?? null };
}

export function useTrainingEmployees(params: TrainingEmployeesParams) {
  return useQuery({
    queryKey: ['training', 'employees', 'list', params],
    queryFn: () => fetchTrainingEmployees(params),
    placeholderData: keepPreviousData,
  });
}

export type TrainingEmployeesListFilters = Omit<TrainingEmployeesParams, 'pageToken'>;

/** Список сотрудников по курсору (`page_token`): страницы догружаются, общее количество не считается. */
export function useTrainingEmployeesInfinite(filters: TrainingEmployeesListFilters) {
  return useInfiniteQuery({
    queryKey: ['training', 'employees', 'list', 'infinite', filters],
    queryFn: ({ pageParam }) => fetchTrainingEmployees({ ...filters, pageToken: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
  });
}

// --- Employee assignments (§5.2.3) ---

export interface EmployeeAssignmentsParams {
  status?: string[];
  programKind?: string[];
  recurrenceModel?: string[];
  levelBased?: boolean;
  includeCompleted?: boolean;
}

async function fetchEmployeeAssignments(
  employeeId: string,
  params: EmployeeAssignmentsParams = {},
): Promise<EmployeeAssignment[]> {
  const sp = new URLSearchParams();
  if (params.status) params.status.forEach((v) => sp.append('status', v));
  if (params.programKind) params.programKind.forEach((v) => sp.append('program_kind', v));
  if (params.recurrenceModel) params.recurrenceModel.forEach((v) => sp.append('recurrence_model', v));
  if (params.levelBased !== undefined) sp.set('level_based', String(params.levelBased));
  if (params.includeCompleted !== undefined) sp.set('include_completed', String(params.includeCompleted));

  const qs = sp.toString();
  const path = `/training/employees/${employeeId}/assignments${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  const parsed = employeeAssignmentsResponseSchema.parse(raw);
  return parsed.assignments;
}

export function useEmployeeAssignments(employeeId: string | null, params: EmployeeAssignmentsParams = {}) {
  return useQuery({
    queryKey: ['training', 'employees', employeeId, 'assignments', params],
    queryFn: () => fetchEmployeeAssignments(employeeId!, params),
    enabled: !!employeeId,
  });
}

// --- Assignment detail (§5.3.1) and events (§5.3.2) ---

async function fetchAssignmentDetail(assignmentId: string): Promise<AssignmentDetail> {
  const raw = await apiFetch<unknown>(`/training/assignments/${assignmentId}`);
  const parsed = assignmentDetailResponseSchema.parse(raw);
  return parsed.assignment;
}

export function useAssignmentDetail(assignmentId: string | null) {
  return useQuery({
    queryKey: ['training', 'assignments', assignmentId],
    queryFn: () => fetchAssignmentDetail(assignmentId!),
    enabled: !!assignmentId,
  });
}

async function fetchAssignmentEvents(assignmentId: string): Promise<AssignmentEvent[]> {
  const raw = await apiFetch<unknown>(`/training/assignments/${assignmentId}/events`);
  const parsed = assignmentEventsResponseSchema.parse(raw);
  return parsed.events;
}

export function useAssignmentEvents(assignmentId: string | null) {
  return useQuery({
    queryKey: ['training', 'assignments', assignmentId, 'events'],
    queryFn: () => fetchAssignmentEvents(assignmentId!),
    enabled: !!assignmentId,
  });
}

// --- Widgets (§5.1.2) ---

async function fetchTrainingSummaryWidget(): Promise<TrainingSummaryWidget> {
  const raw = await apiFetch<unknown>('/training/widgets/summary');
  return trainingSummaryWidgetResponseSchema.parse(raw).widget;
}

export function useTrainingSummaryWidget() {
  return useQuery({
    queryKey: ['training', 'widgets', 'summary'],
    queryFn: fetchTrainingSummaryWidget,
  });
}

async function fetchTrainingRiskWidget(): Promise<TrainingRiskWidget> {
  const raw = await apiFetch<unknown>('/training/widgets/risk');
  return trainingRiskWidgetResponseSchema.parse(raw).widget;
}

export function useTrainingRiskWidget() {
  return useQuery({
    queryKey: ['training', 'widgets', 'risk'],
    queryFn: fetchTrainingRiskWidget,
  });
}

// --- Problem employees (§5.4) ---

export interface ProblemEmployeesParams {
  state?: string[];
  orgUnitId?: string[];
  pageToken?: string;
}

export interface ProblemEmployeesResult {
  items: ProblemEmployeeRow[];
  summary: ProblemEmployeesSummary;
  nextPageToken: string | null;
}

async function fetchProblemEmployees(params: ProblemEmployeesParams): Promise<ProblemEmployeesResult> {
  const sp = new URLSearchParams();
  if (params.state) params.state.forEach((v) => sp.append('state', v));
  if (params.orgUnitId) params.orgUnitId.forEach((v) => sp.append('org_unit_id', v));
  if (params.pageToken) sp.set('page_token', params.pageToken);

  const qs = sp.toString();
  const path = `/training/problem-employees${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  const parsed = problemEmployeesResponseSchema.parse(raw);
  return { items: parsed.items, summary: parsed.summary, nextPageToken: parsed.next_page_token ?? null };
}

export type ProblemEmployeesListFilters = Omit<ProblemEmployeesParams, 'pageToken'>;

export function useProblemEmployeesInfinite(filters: ProblemEmployeesListFilters) {
  return useInfiniteQuery({
    queryKey: ['training', 'problem-employees', 'infinite', filters],
    queryFn: ({ pageParam }) => fetchProblemEmployees({ ...filters, pageToken: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
  });
}

// --- Training matrix ---

export interface TrainingMatrixData {
  positions: MatrixPosition[];
  totalPositions: number;
  totalPrograms: number;
}

async function fetchTrainingMatrix(): Promise<TrainingMatrixData> {
  const raw = await apiFetch<unknown>('/training/matrix');
  const parsed = trainingMatrixResponseSchema.parse(raw);
  return {
    positions: parsed.positions,
    totalPositions: parsed.total_positions,
    totalPrograms: parsed.total_programs,
  };
}

export function useTrainingMatrix() {
  return useQuery({
    queryKey: ['training', 'matrix'],
    queryFn: fetchTrainingMatrix,
    staleTime: 120_000,
  });
}
